'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { HStack } from '@terra/ui-styled-system';
import { Add01Icon, ArrowDown01Icon } from '@terra/ui-icons';
import { Button } from '../../../components/button';
import { MenuWithItems } from '../../../components/styled/menu';
import { BodyDefault } from '../../../components/typography';
import { DOCUMENTATION_MENU_ITEM_VALUE } from './applicationDocumentation.constants';

type DocumentationAddMenuProps = {
  triggerVariant: 'add' | 'add-documentation';
  disabled: boolean;
  onAddFiles: () => void;
  onAddLinks: () => void;
};

export const DocumentationAddMenu = ({
  triggerVariant,
  disabled,
  onAddFiles,
  onAddLinks,
}: DocumentationAddMenuProps) => {
  const t = useTranslations(
    'designSystem.documentsReports.applicationDocumentation',
  );
  const tCommon = useTranslations('designSystem.common');
  const isAddTrigger = triggerVariant === 'add';
  const handleMenuItem = useCallback(
    (value: string) => {
      if (value === DOCUMENTATION_MENU_ITEM_VALUE.addFiles) {
        onAddFiles();

        return;
      }

      if (value === DOCUMENTATION_MENU_ITEM_VALUE.addLinks) {
        onAddLinks();
      }
    },
    [onAddFiles, onAddLinks],
  );

  return (
    <MenuWithItems
      lazyMount
      unmountOnExit
      positioning={{
        strategy: 'fixed',
        placement: isAddTrigger ? 'bottom-start' : 'bottom-end',
      }}
      trigger={
        isAddTrigger ? (
          <Button
            variant="ghost"
            size="sm"
            fontWeight="normal"
            alignSelf="flex-start"
            disabled={disabled}
          >
            <Add01Icon size={16} />
            {tCommon('add')}
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            fontWeight="normal"
            disabled={disabled}
          >
            <HStack gap={1} alignItems="center">
              <BodyDefault>{t('addDocumentation')}</BodyDefault>
              <ArrowDown01Icon size={16} />
            </HStack>
          </Button>
        )
      }
      items={[
        {
          value: DOCUMENTATION_MENU_ITEM_VALUE.addFiles,
          label: <BodyDefault>{t('addFiles')}</BodyDefault>,
          onClick: handleMenuItem,
        },
        {
          value: DOCUMENTATION_MENU_ITEM_VALUE.addLinks,
          label: <BodyDefault>{t('addLinks')}</BodyDefault>,
          onClick: handleMenuItem,
        },
      ]}
    />
  );
};
